import { useTranslation } from 'react-i18next';
import { PlusIcon } from 'lucide-react';
import { Button } from '@monksflow/monks-ui';
import { EmptyState } from '@/shared/ui';

/**
 * The empty, no-results and error states shared by all four taxonomy tabs.
 *
 * Lives here rather than under `TaxonomyList/` because it is no longer one
 * tab's private component: schemas, lists, fields and categories all show the
 * same three states, and three of them showed nothing at all when a filter
 * matched no rows — a bare column header over an empty table, with nothing for
 * assistive technology to announce either.
 *
 * Copy arrives as props rather than being built from an interpolated noun. A
 * frame like `'No {{entity}} found'` cannot be localised — the noun's case,
 * gender and article all depend on the sentence around it — so each caller
 * passes whole sentences it resolved itself. See the i18n convention doc.
 */

interface NoResultsProps {
  title: string;
  description: string;
  /** Resets every filter the calling tab owns. */
  onClear: () => void;
}

/** No rows match the active search/filter. */
export function TaxonomyNoResultsState({ title, description, onClear }: NoResultsProps) {
  const { t } = useTranslation('knowledge');
  return (
    <EmptyState
      role="status"
      data-testid="taxonomy-no-results-state"
      className="flex-1"
      title={title}
      description={description}
    >
      <Button variant="outline" size="default" onClick={onClear}>
        {t('taxonomy.taxonomyStates.clearFilters')}
      </Button>
    </EmptyState>
  );
}

interface EmptyProps {
  title: string;
  description: string;
  /** Rendered in a badge above the title. */
  icon?: React.ReactNode;
  /** One or more calls to action. */
  children?: React.ReactNode;
}

/**
 * Nothing exists yet, and no filter is hiding it.
 *
 * `outline-dashed` is the design system's own variant for this, which is what
 * the three tabs were reproducing by hand with a `border-dashed` utility and a
 * duplicated comment about capping the description width.
 */
export function TaxonomyEmptyState({ title, description, icon, children }: EmptyProps) {
  return (
    <EmptyState
      role="status"
      data-testid="taxonomy-empty-state"
      className="mx-4 flex-1"
      variant="outline-dashed"
      icon={icon}
      title={title}
      description={description}
      descriptionMaxWidth="34rem"
    >
      {children}
    </EmptyState>
  );
}

/** The initial state for the schemas tab, whose only action is "add". */
export function TaxonomySchemaEmptyState({ onCreate }: { onCreate?: () => void }) {
  const { t } = useTranslation('knowledge');
  return (
    <EmptyState
      role="status"
      data-testid="taxonomy-empty-state"
      className="flex-1"
      title={t('taxonomy.taxonomyStates.taxonomy')}
      description={t('taxonomy.taxonomyStates.defineVersionNamingSchemas')}
    >
      {onCreate && (
        <Button variant="default" size="default" onClick={onCreate}>
          <PlusIcon className="size-4" />
          {t('taxonomy.taxonomyStates.addSchema')}
        </Button>
      )}
    </EmptyState>
  );
}

/**
 * Query failed and there is nothing cached to show.
 *
 * `title` is a prop because the four tabs name different things, and the i18n
 * guide forbids interpolating a translated noun into a translated frame — so each
 * caller passes a whole sentence it resolved itself.
 */
export function TaxonomyErrorState({ title, onRetry }: { title?: string; onRetry: () => void }) {
  const { t } = useTranslation('knowledge');
  return (
    <EmptyState
      role="alert"
      data-testid="taxonomy-error-state"
      className="flex-1"
      title={title ?? t('taxonomy.taxonomyStates.couldnTLoadSchemas')}
      description={t('taxonomy.taxonomyStates.somethingWentWrongWhileLoading')}
    >
      <Button variant="outline" size="default" onClick={onRetry}>
        {t('taxonomy.taxonomyStates.tryAgain')}
      </Button>
    </EmptyState>
  );
}
